import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Calendar } from "@repo/ui/components/calendar";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { Field, FieldDescription, FieldLabel } from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@repo/ui/components/sheet";
import { Skeleton } from "@repo/ui/components/skeleton";
import { Switch } from "@repo/ui/components/switch";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { format } from "date-fns";
import { CalendarIcon, Plus, SaveIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { DeliverySection } from "@/components/scheduling/delivery-section";
import {
  SessionProgressSelect,
  type SessionProgress,
} from "@/components/scheduling/session-progress-select";
import { sessionTypes, type SessionType } from "@/lib/scheduling";
import { getErrorMessage } from "@/lib/utils";

export const Route = createFileRoute("/_authed/staff-photo-shoots/walk-ins")({
  head: () => ({ meta: [{ title: "Walk-ins" }] }),
  component: WalkInsPage,
});

const sessionLabels: Record<SessionType, string> = {
  headshot_light: "Fullbody Light",
  headshot_dark: "Portrait Dark",
  editorial_solo: "Editorial Solo",
  editorial_team: "Editorial Team",
};

type WalkIn = (typeof api.scheduling.listWalkIns)["_returnType"]["walkIns"][number];

function DatePicker({
  value,
  onValueChange,
  id,
}: {
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type="date"
        required
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className="[&::-webkit-calendar-picker-indicator]:hidden"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={<Button variant="outline" size="icon" type="button" aria-label="Pick a date" />}
        >
          <CalendarIcon className="size-4" />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            captionLayout="dropdown"
            mode="single"
            selected={selected && !Number.isNaN(selected.getTime()) ? selected : undefined}
            onSelect={(date) => {
              if (date) onValueChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// TODO: This needs to be refactored to use tanstack/form for form validation and zod for the validation of the inputs
// TODO: The code needs to be refactored to simplify the editting sheets.

function WalkInsPage() {
  const confirm = useConfirmationDialog();
  const data = useQuery(api.scheduling.listWalkIns);
  const updateWalkIn = useMutation(api.scheduling.updateWalkIn);
  const deleteWalkIn = useMutation(api.scheduling.deleteWalkIn);
  const addWalkIn = useMutation(api.scheduling.addWalkIn);
  const [selected, setSelected] = React.useState<WalkIn | null>(null);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [type, setType] = React.useState<SessionType>("headshot_light");
  const [progress, setProgress] = React.useState<SessionProgress>("booked");
  const [date, setDate] = React.useState("");
  const [exportFolderByName, setExportFolderByName] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newEmail, setNewEmail] = React.useState("");
  const [newType, setNewType] = React.useState<SessionType>("headshot_light");
  const [newDate, setNewDate] = React.useState("");
  const [newExportFolderByName, setNewExportFolderByName] = React.useState(false);
  const [addSaving, setAddSaving] = React.useState(false);

  if (!data) return <Skeleton className="m-3 h-[calc(100svh-5rem)] rounded-xl" />;

  const groups: Record<string, WalkIn[]> = {};
  for (const walkIn of data.walkIns) {
    (groups[walkIn.localDate] ??= []).push(walkIn);
  }
  const dateList = Object.entries(groups).toSorted(([a], [b]) => b.localeCompare(a));

  const openWalkIn = (walkIn: WalkIn) => {
    setSelected(walkIn);
    setName(walkIn.name);
    setEmail(walkIn.email);
    setType(walkIn.sessionType);
    setProgress(
      walkIn.photosReady ? "ready" : walkIn.deliveryStatus === "shot" ? "shot" : "booked",
    );
    setDate(walkIn.localDate);
    setExportFolderByName(walkIn.exportFolderByName);
  };

  const openAdd = () => {
    const todayKey = new Date().toLocaleDateString("sv-SE");
    const sortedDates = [...data.activeDates].toSorted();
    const defaultDate = sortedDates.includes(todayKey)
      ? todayKey
      : (sortedDates.find((d) => d >= todayKey) ?? sortedDates.at(-1) ?? "");
    if (!defaultDate) return;
    setNewName("");
    setNewEmail("");
    setNewType("headshot_light");
    setNewDate(defaultDate);
    setNewExportFolderByName(false);
    setAdding(true);
  };

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newDate) return;
    setAddSaving(true);
    try {
      await addWalkIn({
        name: newName.trim(),
        email: newEmail.trim(),
        sessionType: newType,
        localDate: newDate,
        exportFolderByName: newExportFolderByName,
      });
      setAdding(false);
      toast.success("Walk-in added.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add the walk-in."));
    } finally {
      setAddSaving(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !date) return;
    setSaving(true);
    try {
      await updateWalkIn({
        id: selected.id,
        name,
        email,
        sessionType: type,
        localDate: date,
        exportFolderByName,
        ...(selected.deliveryStatus === "delivered" ? {} : { progress }),
      });
      setSelected((current) =>
        current
          ? { ...current, name, email, sessionType: type, localDate: date, exportFolderByName }
          : current,
      );
      toast.success("Walk-in updated.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not save the walk-in."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (walkIn: WalkIn) => {
    const accepted = await confirm({
      title: "Delete this walk-in?",
      description: `${walkIn.name} will be removed and their QR code will stop working.`,
      confirmText: "Delete",
    });
    if (!accepted) return;
    setDeleting(true);
    try {
      await deleteWalkIn({ id: walkIn.id });
      setSelected(null);
      toast.success("Walk-in deleted.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete the walk-in."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Walk-ins</h1>
          <p className="text-sm text-muted-foreground">
            People added on the iPad outside the booking schedule.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{data.walkIns.length} total</Badge>
          <Button size="sm" onClick={openAdd}>
            <Plus /> New walk-in
          </Button>
        </div>
      </div>

      {dateList.length === 0 ? (
        <p className="text-sm text-muted-foreground">No walk-ins yet.</p>
      ) : (
        <div className="space-y-6">
          {dateList.map(([localDate, items]) => (
            <section key={localDate} className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground">
                {format(new Date(`${localDate}T00:00:00`), "EEE, MMM d, yyyy")} · {items.length}
              </h2>
              <div className="divide-y rounded-lg border bg-card">
                {items.map((walkIn) => (
                  <button
                    key={String(walkIn.id)}
                    type="button"
                    onClick={() => openWalkIn(walkIn)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{walkIn.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {walkIn.sessionLabel} · {walkIn.email}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {walkIn.localStartTime}
                      </span>
                      {walkIn.deliveryStatus === "delivered" ? (
                        <Badge className="border-emerald-500/40 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                          Delivered
                        </Badge>
                      ) : walkIn.photosReady ? (
                        <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-600 dark:text-amber-400">
                          Ready
                        </Badge>
                      ) : walkIn.deliveryStatus === "shot" ? (
                        <Badge className="border-sky-500/40 bg-sky-500/15 text-sky-600 dark:text-sky-400">
                          Shot
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Not shot</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <Sheet open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="flex h-full flex-col overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-md">
          {selected ? (
            <form className="flex h-full flex-col gap-4" onSubmit={(event) => void submit(event)}>
              <SheetHeader className="border-b pr-8">
                <SheetTitle>Edit walk-in</SheetTitle>
                <SheetDescription>
                  Update the participant or move them to another day. No confirmation email is sent.
                </SheetDescription>
              </SheetHeader>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="walkin-name">Name</FieldLabel>
                    <Input
                      id="walkin-name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      autoComplete="name"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="walkin-email">Email</FieldLabel>
                    <Input
                      id="walkin-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      required
                    />
                  </Field>
                </div>
                <Field>
                  <FieldLabel>Session</FieldLabel>
                  <Select
                    value={type}
                    onValueChange={(next) => next && setType(next as SessionType)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sessionTypes.map((sessionType) => (
                        <SelectItem key={sessionType} value={sessionType}>
                          {sessionLabels[sessionType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel htmlFor="walkin-status">Status</FieldLabel>
                  <SessionProgressSelect
                    id="walkin-status"
                    value={progress}
                    onValueChange={setProgress}
                    delivered={selected.deliveryStatus === "delivered"}
                  />
                </Field>
                <Field className="flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-1">
                    <FieldLabel htmlFor="walkin-export-folder-by-name">
                      Export folder by name
                    </FieldLabel>
                    <FieldDescription>
                      Use the participant name instead of their email for the Renamer export folder.
                    </FieldDescription>
                  </div>
                  <Switch
                    id="walkin-export-folder-by-name"
                    checked={exportFolderByName}
                    onCheckedChange={setExportFolderByName}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="walkin-date">Date</FieldLabel>
                  <DatePicker id="walkin-date" value={date} onValueChange={setDate} />
                </Field>
                <DeliverySection email={selected.email} localDate={selected.localDate} />
              </div>

              <div className="mt-auto flex items-center justify-between gap-2 border-t p-4">
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleting || saving}
                  onClick={() => void handleDelete(selected)}
                >
                  <Trash2 /> Delete
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <SaveIcon className="animate-pulse" /> : <SaveIcon />}
                  Save changes
                </Button>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet open={adding} onOpenChange={(open) => !open && setAdding(false)}>
        <SheetContent className="flex h-full flex-col overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-md">
          <form className="flex h-full flex-col gap-4" onSubmit={(event) => void handleAdd(event)}>
            <SheetHeader className="border-b pr-8">
              <SheetTitle>Add walk-in</SheetTitle>
              <SheetDescription>
                Add a person outside the booking schedule. No confirmation email is sent.
              </SheetDescription>
            </SheetHeader>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4">
              <Field>
                <FieldLabel htmlFor="new-walkin-name">Name</FieldLabel>
                <Input
                  id="new-walkin-name"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  autoComplete="name"
                  autoFocus
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="new-walkin-email">Email</FieldLabel>
                <Input
                  id="new-walkin-email"
                  type="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </Field>
              <Field className="flex-row items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-1">
                  <FieldLabel htmlFor="new-walkin-export-folder-by-name">
                    Export folder by name
                  </FieldLabel>
                  <FieldDescription>
                    Use the participant name instead of their email for the Renamer export folder.
                  </FieldDescription>
                </div>
                <Switch
                  id="new-walkin-export-folder-by-name"
                  checked={newExportFolderByName}
                  onCheckedChange={setNewExportFolderByName}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="new-walkin-session">Session</FieldLabel>
                <Select
                  value={newType}
                  onValueChange={(next) => next && setNewType(next as SessionType)}
                >
                  <SelectTrigger id="new-walkin-session" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionTypes.map((sessionType) => (
                      <SelectItem key={sessionType} value={sessionType}>
                        {sessionLabels[sessionType]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="new-walkin-date">Date</FieldLabel>
                <DatePicker id="new-walkin-date" value={newDate} onValueChange={setNewDate} />
              </Field>
            </div>

            <div className="mt-auto flex items-center justify-end gap-2 border-t p-4">
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addSaving}>
                {addSaving ? <SaveIcon className="animate-pulse" /> : <Plus />}
                Add walk-in
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
